import {
  FloatingFocusManager,
  FloatingPortal,
  useMergeRefs,
  useTransitionStyles,
} from '@floating-ui/react';
import { Close } from '#icons/Close';
import { Button } from '../../Input/Button';
import { IconButton } from '../../Input/IconButton';
import {
  HideOnPrint,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalInner,
  ModalScrollableContent,
  OverlayWrapper,
  PrintGlobals,
} from './styles';
import { ModalContext, useModal, useModalContext } from './ModalContext';
import { type ModalOptions } from './types';
import type React from 'react';

export function Modal({
  children,
  onClose,
  onOpenChange,
  reverseButtonOrder,
  ...options
}: {
  children: React.ReactNode;
} & ModalOptions) {
  const _onOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose?.();
    }
  };

  const dialog = useModal({ ...options, onClose, onOpenChange: onOpenChange ?? _onOpenChange });

  return (
    <ModalContext value={dialog}>
      <ModalContent reverseButtonOrder={reverseButtonOrder}>{children}</ModalContent>
    </ModalContext>
  );
}

type ModalContentProps = React.HTMLProps<HTMLDivElement> & {
  reverseButtonOrder?: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

function ModalContent({ reverseButtonOrder, ref: propRef, ...props }: ModalContentProps) {
  const {
    context: floatingContext,
    getFloatingProps,
    primaryAction,
    secondaryAction,
    onClose,
    disableClose,
    maxWidth,
    minWidth,
    constrainToViewport,
    fullScreen,
    refs,
  } = useModalContext();
  const ref = useMergeRefs([
    (node) => {
      refs.setFloating(node);
    },
    propRef,
  ]);

  const { styles, isMounted } = useTransitionStyles(floatingContext, {
    duration: {
      open: 200,
      close: 250,
    },
  });

  if (!floatingContext.open && !isMounted) return null;

  const PrimaryButton = primaryAction && (
    <Button
      variant="primary"
      preventDoubleClick={true}
      onClick={() => {
        void primaryAction.action();
      }}
      icon={primaryAction.icon}
      iconPlacement="after"
      isLoading={primaryAction.isLoading}
      disabled={primaryAction.disabled}
      data-testid="primary-action-button"
    >
      {primaryAction.label}
    </Button>
  );
  const SecondaryButton = secondaryAction && (
    <Button
      variant="tertiary"
      preventDoubleClick={true}
      onClick={() => {
        void secondaryAction.action();
      }}
      isLoading={secondaryAction.isLoading}
      disabled={secondaryAction.disabled}
      data-testid="secondary-action-button"
    >
      {secondaryAction.label}
    </Button>
  );

  return (
    <FloatingPortal id="floating-ui-portal-root">
      <OverlayWrapper
        style={styles}
        lockScroll
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <FloatingFocusManager context={floatingContext}>
          <ModalBody
            ref={ref}
            {...getFloatingProps(props)}
            style={styles}
            fullScreen={fullScreen}
            maxWidth={maxWidth}
            minWidth={minWidth}
            constrainToViewport={constrainToViewport}
            data-testid="modal"
          >
            <ModalScrollableContent constrainToViewport={constrainToViewport}>
              {!disableClose && (
                <ModalHeader>
                  <HideOnPrint>
                    <IconButton
                      variant="secondary"
                      onClick={() => {
                        onClose?.();
                      }}
                      data-testid="close-modal-button"
                    >
                      <Close />
                    </IconButton>
                  </HideOnPrint>
                </ModalHeader>
              )}
              <ModalInner topPadding={disableClose}>{props.children}</ModalInner>
            </ModalScrollableContent>
            {(primaryAction ?? secondaryAction) && (
              <ModalFooter>
                {reverseButtonOrder ? (
                  <>
                    {PrimaryButton}
                    {SecondaryButton}
                  </>
                ) : (
                  <>
                    {SecondaryButton}
                    {PrimaryButton}
                  </>
                )}
              </ModalFooter>
            )}
          </ModalBody>
        </FloatingFocusManager>
      </OverlayWrapper>
      <PrintGlobals />
    </FloatingPortal>
  );
}
